import { ActivityGraph } from "@thilke/ui/components/activity-graph";
import { AiCopyButton } from "@thilke/ui/components/ai-copy-button";
import { CIBadge } from "@thilke/ui/components/ci-badge";
import { CodeBlock } from "@thilke/ui/components/code-block";
import { CodeBlockCommand } from "@thilke/ui/components/code-block-command";
import { CodeLine } from "@thilke/ui/components/code-line";
import { ColorPalette } from "@thilke/ui/components/color-palette";
import { CommitGraph } from "@thilke/ui/components/commit-graph";
import {
  ContributorGrid,
  ContributorList,
} from "@thilke/ui/components/contributor-grid";
import { CronSchedule } from "@thilke/ui/components/cron-schedule";
import { DiffViewer } from "@thilke/ui/components/diff-viewer";
import { DiscordBadge } from "@thilke/ui/components/discord-badge";
import { DownloadSparkline } from "@thilke/ui/components/download-sparkline";
import { EnvTable } from "@thilke/ui/components/env-table";
import { FileTree } from "@thilke/ui/components/file-tree";
import { GitHubButtonGroup } from "@thilke/ui/components/github-button-group";
import { GitHubStarsButton } from "@thilke/ui/components/github-stars-button";
import { JsonViewer } from "@thilke/ui/components/json-viewer";
import { KbdCombo } from "@thilke/ui/components/kbd";
import { LicenseBadge } from "@thilke/ui/components/license-badge";
import { LogViewerTerminal } from "@thilke/ui/components/log-viewer";
import { LogoCloud } from "@thilke/ui/components/logo-cloud";
import { MasonryGrid } from "@thilke/ui/components/masonry-grid";
import { NpmBadge } from "@thilke/ui/components/npm-badge";
import { ReleaseBadge } from "@thilke/ui/components/release-badge";
import { RepoCard } from "@thilke/ui/components/repo-card";
import { RequestViewer } from "@thilke/ui/components/request-viewer";
import { StatusIndicator } from "@thilke/ui/components/status-indicator";
import { TestimonialCard } from "@thilke/ui/components/testimonial";
import { TipJarCompact } from "@thilke/ui/components/tip-jar";
import {
  useBalancedWidth,
  usePretextWithSegments,
} from "@thilke/ui/lib/use-pretext";
import * as React from "react";

const codeBlockSample = `import { Button } from "@thilke/ui/components/button";
import { CodeBlock } from "@thilke/ui/components/code-block";

export function RegistryAction() {
  return (
    <Button variant="outline" size="sm">
      Copy install command
    </Button>
  );
}`;

const codeLineSample = `pnpm dlx shadcn@latest add @thilke/kbd`;

const previewRepo = {
  fullName: "thilke/ui",
  stars: 113,
  forks: 12,
  watchers: 27,
  issues: 3,
};

const previewRepoData = {
  ...previewRepo,
  description:
    "Shared shadcn-compatible primitives, blocks, and registry previews.",
  language: "TypeScript",
  languageColor: "#3178c6",
  license: "MIT",
  topics: ["shadcn", "registry", "ui"],
  updatedAt: "2026-06-11T06:30:00Z",
  isArchived: false,
  isFork: false,
};

const previewContributors = [
  {
    login: "thilke",
    avatarUrl: "https://github.com/identicons/thilke.png?size=96",
    profileUrl: "https://git.thilke.com",
    contributions: 418,
  },
  {
    login: "ui-bot",
    avatarUrl: "https://github.com/identicons/ui-bot.png?size=96",
    profileUrl: "https://ui.thilke.com",
    contributions: 96,
  },
  {
    login: "registry-worker",
    avatarUrl: "https://github.com/identicons/registry-worker.png?size=96",
    profileUrl: "https://ui.thilke.com/registry.json",
    contributions: 42,
  },
];

const previewCi = {
  status: "success" as const,
  workflowName: "ui-registry",
  branch: "main",
  url: "https://ui.thilke.com",
  startedAt: "2026-06-11T06:20:00Z",
  durationSeconds: 86,
};

const previewRelease = {
  tag: "ui-v0.114.0",
  name: "registry catalog update",
  url: "https://ui.thilke.com/registry.json",
  publishedAt: "2026-06-11T06:28:00Z",
  preRelease: false,
  assetCount: 31,
};

const previewDiscord = {
  id: "thilke-ui",
  name: "Thilke UI",
  instantInvite: "https://ui.thilke.com",
  onlineCount: 42,
  memberCount: 256,
};

const previewNpm = {
  name: "@thilke/ui",
  version: "0.0.114",
  description: "Shared shadcn-compatible primitives and registry blocks.",
  license: "MIT",
  weeklyDownloads: 2360,
  lastPublish: "2026-06-11T06:28:00Z",
  homepage: "https://ui.thilke.com",
};

const previewDownloads = [
  { day: "2026-05-15", downloads: 42 },
  { day: "2026-05-16", downloads: 58 },
  { day: "2026-05-17", downloads: 54 },
  { day: "2026-05-18", downloads: 73 },
  { day: "2026-05-19", downloads: 91 },
  { day: "2026-05-20", downloads: 88 },
  { day: "2026-05-21", downloads: 120 },
  { day: "2026-05-22", downloads: 144 },
  { day: "2026-05-23", downloads: 132 },
  { day: "2026-05-24", downloads: 168 },
  { day: "2026-05-25", downloads: 184 },
  { day: "2026-05-26", downloads: 203 },
];

const pretextPreviewText =
  "Pretext measures text before layout, so long copy can balance into readable cards without guessing line breaks.";

const masonryItems = [
  {
    title: "Balanced text",
    text: "Pretext measures the copy first, then lets the card size match the real line count.",
  },
  {
    title: "Registry preview",
    text: "The component now renders directly in the registry preview instead of falling back to install instructions.",
  },
  {
    title: "Dense UI",
    text: "Masonry cards keep operational notes compact while preserving semantic tokens and shadcn-compatible anatomy.",
  },
  {
    title: "Source-backed",
    text: "This preview uses the exported component path exported by @thilke/ui.",
  },
];

function AsyncPreviewFrame({
  node,
  error,
}: {
  node: React.ReactNode;
  error: string | null;
}) {
  if (error) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        {error}
      </div>
    );
  }

  if (node) return node;

  return (
    <div className="grid gap-2 rounded-xl border bg-card p-4 shadow-sm">
      <div className="h-4 w-28 animate-pulse rounded bg-muted" />
      <div className="h-4 w-full animate-pulse rounded bg-muted" />
      <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
    </div>
  );
}

function AsyncNodePreview({
  render,
  label,
}: {
  render: () => Promise<React.ReactNode>;
  label: string;
}) {
  const [node, setNode] = React.useState<React.ReactNode>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;

    void render()
      .then((rendered) => {
        if (mounted) setNode(rendered);
      })
      .catch(() => {
        if (mounted) setError(`Unable to render ${label} preview.`);
      });

    return () => {
      mounted = false;
    };
  }, [render, label]);

  return <AsyncPreviewFrame node={node} error={error} />;
}

function AsyncCodeBlockPreview() {
  const [node, setNode] = React.useState<React.ReactNode>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;

    void CodeBlock({
      code: codeBlockSample,
      language: "tsx",
      title: "registry-action.tsx",
      overflow: "collapsible",
      maxHeight: 220,
    })
      .then((rendered) => {
        if (mounted) setNode(rendered);
      })
      .catch(() => {
        if (mounted) setError("Unable to render Shiki code block preview.");
      });

    return () => {
      mounted = false;
    };
  }, []);

  return <AsyncPreviewFrame node={node} error={error} />;
}

function AsyncCodeLinePreview() {
  const [node, setNode] = React.useState<React.ReactNode>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;

    void CodeLine({
      code: codeLineSample,
      language: "bash",
      label: "Install",
    })
      .then((rendered) => {
        if (mounted) setNode(rendered);
      })
      .catch(() => {
        if (mounted) setError("Unable to render Shiki code line preview.");
      });

    return () => {
      mounted = false;
    };
  }, []);

  return <AsyncPreviewFrame node={node} error={error} />;
}

export function CodeBlockDemo() {
  return <AsyncCodeBlockPreview />;
}

export function CodeBlockCommandDemo() {
  return (
    <CodeBlockCommand
      pnpm="pnpm dlx shadcn@latest add @thilke/code-block"
      npm="npx shadcn@latest add @thilke/code-block"
      yarn="yarn dlx shadcn@latest add @thilke/code-block"
      bun="bunx shadcn@latest add @thilke/code-block"
      shadcn="shadcn add @thilke/code-block"
      show={["pnpm", "npm", "yarn", "bun", "shadcn"]}
    />
  );
}

export function CodeLineDemo() {
  return <AsyncCodeLinePreview />;
}

export function DiffViewerDemo() {
  return (
    <DiffViewer
      oldCode={'export const tone = "muted";\nrender(tone);'}
      newCode={'export const tone = "accent";\nrender(tone);'}
      language="ts"
    />
  );
}

export function AiCopyButtonDemo() {
  return (
    <AiCopyButton
      value="Summarize this registry component adoption."
      label="Copy prompt"
    />
  );
}

export function ColorPaletteDemo() {
  return (
    <ColorPalette
      colors={[
        { name: "Deep Coal", value: "#121212" },
        { name: "Dark Iron", value: "#1E1E1E" },
        { name: "Molten Orange", value: "#FF5A1F" },
        { name: "Oxidized Copper", value: "#4DB6AC" },
      ]}
      columns={4}
    />
  );
}

export function FileTreeDemo() {
  return (
    <FileTree
      iconStyle="colored"
      tree={[
        {
          name: "packages",
          children: [{ name: "ui", children: [{ name: "registry.json" }] }],
        },
        {
          name: "apps",
          children: [{ name: "ui-registry", children: [{ name: "src" }] }],
        },
      ]}
    />
  );
}

export function KbdDemo() {
  return <KbdCombo keys={["⌘", "K"]} />;
}

export function ActivityGraphDemo() {
  const today = new Date();
  const data = Array.from({ length: 28 }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() - index);
    return { date: date.toISOString().slice(0, 10), count: (index * 7) % 9 };
  });
  return <ActivityGraph data={data} weeks={8} blockSize={12} />;
}

export function CIBadgeDemo() {
  const render = React.useCallback(
    () =>
      CIBadge({
        owner: "thilke",
        repo: "ui",
        layout: "card",
        data: previewCi,
        showBranch: true,
        showDuration: true,
        showWorkflow: true,
      }),
    [],
  );

  return <AsyncNodePreview render={render} label="CI badge" />;
}

export function CommitGraphDemo() {
  return (
    <CommitGraph
      commits={[
        {
          hash: "a1b2c3d",
          parents: ["9f8e7d6"],
          message: "Adopt registry components",
          author: { name: "Thilke" },
          date: "2026-06-11",
        },
        {
          hash: "9f8e7d6",
          parents: [],
          message: "Prepare registry metadata",
          author: { name: "Thilke" },
          date: "2026-06-10",
        },
      ]}
    />
  );
}

export function ContributorGridDemo() {
  const render = React.useCallback(async () => {
    const [grid, list] = await Promise.all([
      ContributorGrid({
        owner: "thilke",
        repo: "ui",
        contributors: previewContributors,
        max: 3,
        size: "lg",
        showContributions: true,
      }),
      ContributorList({
        owner: "thilke",
        repo: "ui",
        contributors: previewContributors,
        max: 3,
        title: "Top contributors",
      }),
    ]);

    return (
      <div className="grid gap-4">
        {grid}
        {list}
      </div>
    );
  }, []);

  return <AsyncNodePreview render={render} label="contributor grid" />;
}

export function GitHubButtonGroupDemo() {
  const render = React.useCallback(
    () =>
      GitHubButtonGroup({
        owner: "thilke",
        repo: "ui",
        showRepo: true,
        data: previewRepo,
        metrics: ["stars", "forks", "issues"],
        iconStyle: "github",
      }),
    [],
  );

  return <AsyncNodePreview render={render} label="GitHub button group" />;
}

export function GitHubStarsButtonDemo() {
  const render = React.useCallback(
    () =>
      GitHubStarsButton({
        owner: "thilke",
        repo: "ui",
        stars: previewRepo.stars,
        showRepo: true,
        iconStyle: "github",
        variant: "outline",
      }),
    [],
  );

  return <AsyncNodePreview render={render} label="GitHub stars button" />;
}

export function ReleaseBadgeDemo() {
  const render = React.useCallback(
    () =>
      ReleaseBadge({
        owner: "thilke",
        repo: "ui",
        layout: "card",
        data: previewRelease,
        showDate: true,
        showAssets: true,
      }),
    [],
  );

  return <AsyncNodePreview render={render} label="release badge" />;
}

export function DiscordBadgeDemo() {
  const render = React.useCallback(
    () =>
      DiscordBadge({
        serverId: previewDiscord.id,
        layout: "card",
        iconStyle: "discord",
        showOnline: true,
        data: previewDiscord,
      }),
    [],
  );

  return <AsyncNodePreview render={render} label="Discord badge" />;
}

export function DownloadSparklineDemo() {
  const render = React.useCallback(
    () =>
      DownloadSparkline({
        package: "@thilke/ui",
        data: previewDownloads,
        variant: "area",
        showLabel: true,
        showTrend: true,
        showDateRange: true,
        showBaseline: true,
        width: 220,
        height: 56,
      }),
    [],
  );

  return <AsyncNodePreview render={render} label="download sparkline" />;
}

export function LicenseBadgeDemo() {
  const render = React.useCallback(
    () =>
      LicenseBadge({
        license: "MIT",
        layout: "card",
        showCategory: true,
        showDescription: true,
      }),
    [],
  );

  return <AsyncNodePreview render={render} label="license badge" />;
}

export function NpmBadgeDemo() {
  const render = React.useCallback(
    () =>
      NpmBadge({
        package: "@thilke/ui",
        layout: "card",
        iconStyle: "npm",
        showDownloads: true,
        showLicense: true,
        showLastPublish: true,
        data: previewNpm,
      }),
    [],
  );

  return <AsyncNodePreview render={render} label="npm badge" />;
}

export function RepoCardDemo() {
  const render = React.useCallback(
    () =>
      RepoCard({
        owner: "thilke",
        repo: "ui",
        data: previewRepoData,
        showTopics: true,
        showLicense: true,
        showUpdated: true,
      }),
    [],
  );

  return <AsyncNodePreview render={render} label="repo card" />;
}

export function CronScheduleDemo() {
  return (
    <CronSchedule
      expression="*/15 9-17 * * 1-5"
      title="Weekday sync"
      showNextRuns={3}
    />
  );
}

export function EnvTableDemo() {
  return (
    <EnvTable
      title="Environment"
      variables={[
        {
          key: "VITE_REGISTRY_URL",
          value: "https://ui.thilke.com",
          environment: "production",
        },
        { key: "GITHUB_TOKEN", value: "ghp_example", environment: "secret" },
      ]}
    />
  );
}

export function JsonViewerDemo() {
  return (
    <JsonViewer
      title="registry item"
      data={{ name: "kbd", type: "registry:component", source: "/ui" }}
    />
  );
}

export function LogViewerDemo() {
  return (
    <LogViewerTerminal
      title="deploy.log"
      maxHeight={180}
      entries={[
        {
          level: "info",
          message: "registry build complete",
          timestamp: "2026-06-11T06:00:00Z",
        },
        {
          level: "info",
          message: "114 items published",
          timestamp: "2026-06-11T06:01:00Z",
        },
      ]}
    />
  );
}

export function RequestViewerDemo() {
  return (
    <RequestViewer
      request={{
        method: "GET",
        url: "https://ui.thilke.com/r/kbd.json",
        status: 200,
        statusText: "OK",
        requestHeaders: [{ name: "accept", value: "application/json" }],
        responseHeaders: [{ name: "content-type", value: "application/json" }],
        responseBody: '{"name":"kbd"}',
        timing: [{ label: "total", duration: 42 }],
      }}
    />
  );
}

export function LogoCloudDemo() {
  return (
    <LogoCloud title="Registry sources">
      <span className="rounded-md border bg-muted px-3 py-2 text-sm font-medium">
        shadcn
      </span>
      <span className="rounded-md border bg-muted px-3 py-2 text-sm font-medium">
        Components
      </span>
      <span className="rounded-md border bg-muted px-3 py-2 text-sm font-medium">
        /ui
      </span>
    </LogoCloud>
  );
}

export function TestimonialDemo() {
  return (
    <TestimonialCard
      testimonial={{
        quote: "The registry now installs real source-backed components.",
        author: "Thilke UI",
        role: "Design system",
        company: "Thilke",
        rating: 5,
      }}
    />
  );
}

export function StatusIndicatorDemo() {
  return <StatusIndicator status="operational" />;
}

export function TipJarDemo() {
  return (
    <TipJarCompact
      provider="ethereum"
      address="0x742d35Cc6634C0532925a3b844Bc454e4438f44e"
      token="ETH"
    />
  );
}

export function PretextDemo() {
  const prepared = usePretextWithSegments(
    pretextPreviewText,
    "16px/24px Signika, ui-sans-serif, system-ui",
  );
  const balancedWidth = useBalancedWidth(prepared, 380);

  return (
    <div className="grid gap-3 rounded-xl border bg-card p-4 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Balanced width: {balancedWidth || 380}px
      </div>
      <p
        className="text-balance text-lg font-semibold leading-snug tracking-tight"
        style={{ maxWidth: balancedWidth || 380 }}
      >
        {pretextPreviewText}
      </p>
    </div>
  );
}

export function MasonryGridDemo() {
  return (
    <div className="min-h-72 w-full">
      <MasonryGrid items={masonryItems} columns={2} />
    </div>
  );
}
